import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSession } from '../lib/auth.js';

const API_BASE = (import.meta.env?.VITE_API_BASE || '/api').replace(/\/$/, '');

/**
 * AdminCmdK — global ⌘K (or Ctrl+K) palette for Netcom staff.
 *
 * Only mounts when session.isNetcomAdmin === true. Catches Cmd+K
 * anywhere in the app, opens an overlay, debounces input, queries
 * /admin/search, and routes on selection.
 */
export default function AdminCmdK() {
  const session = getSession();
  const isStaff = !!session?.isNetcomAdmin;
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [hi, setHi] = useState(0);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // ⌘K / Ctrl+K listener — only when admin.
  useEffect(() => {
    if (!isStaff) return;
    const onKey = (e) => {
      const isCmdK = (e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K');
      if (isCmdK) {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isStaff, open]);

  // Focus on open
  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 0); }, [open]);

  // Debounced search
  useEffect(() => {
    if (!open || q.trim().length < 2) { setRows([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const s = getSession();
        const res = await fetch(`${API_BASE}/admin/search?q=${encodeURIComponent(q.trim())}`, {
          headers: { 'Authorization': `Bearer ${s?.idToken || s?.accessToken || ''}` },
        });
        if (!res.ok) throw new Error('Search failed');
        const data = await res.json();
        setRows(data.results || []);
        setHi(0);
      } catch { setRows([]); } finally { setLoading(false); }
    }, 180);
    return () => clearTimeout(t);
  }, [q, open]);

  if (!isStaff || !open) return null;

  function go(row) {
    setOpen(false); setQ(''); setRows([]);
    navigate(row.href);
  }

  function onInputKey(e) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setHi((h) => Math.min(h + 1, rows.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    if (e.key === 'Enter' && rows[hi]) { e.preventDefault(); go(rows[hi]); }
  }

  return (
    <div role="dialog" aria-modal="true" className="fixed inset-0 z-[200] bg-slate-900/40 flex items-start justify-center p-4 pt-[15vh]" onClick={() => setOpen(false)}>
      <div className="bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100">
          <svg className="w-5 h-5 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
          <input
            ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onInputKey}
            placeholder="Search customers · dishes · SIMs · pages…"
            className="flex-1 outline-none text-base bg-transparent"
          />
          <kbd className="hidden sm:inline-flex text-[10px] font-semibold px-1.5 py-0.5 border border-slate-200 rounded text-slate-500 bg-slate-50">ESC</kbd>
        </div>
        <ul className="max-h-[55vh] overflow-y-auto">
          {loading && q.length >= 2 ? (
            <li className="px-4 py-6 text-center text-sm text-slate-400">Searching…</li>
          ) : rows.length === 0 ? (
            <li className="px-4 py-6 text-center text-sm text-slate-400">
              {q.length < 2 ? 'Type at least 2 characters' : 'No matches'}
            </li>
          ) : (
            rows.map((r, i) => (
              <li
                key={`${r.type}-${r.href}-${i}`}
                onMouseEnter={() => setHi(i)}
                onClick={() => go(r)}
                className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer ${i === hi ? 'bg-[var(--color-brand-50,#f0f9ff)]' : ''}`}
              >
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 w-12 flex-shrink-0">{r.type}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-900 truncate">{r.label}</div>
                  <div className="text-[11px] text-slate-500 truncate">{r.sublabel}</div>
                </div>
                <span className="text-[10px] text-slate-400">↵</span>
              </li>
            ))
          )}
        </ul>
        <div className="px-4 py-2 border-t border-slate-100 bg-slate-50 text-[10px] text-slate-500 flex items-center gap-3">
          <kbd className="font-semibold">↑↓</kbd> navigate
          <kbd className="font-semibold">↵</kbd> select
          <kbd className="font-semibold">esc</kbd> close
          <span className="ml-auto">Admin search · {rows.length} results</span>
        </div>
      </div>
    </div>
  );
}
